/* eslint-env node */
import {useContext} from 'react';
import {MobXProviderContext} from 'mobx-react';
import {useParams} from 'react-router-dom';
import {APP_PERMISSIONS} from '../../data/permissions';
import type NavigationStore from '../../navigation/store/NavigationStore';

/** Studio's pages, all inside the organization's workspace. */
export function studioPaths(organizationId?: string) {
  const root = `/manage/${organizationId}/microreels/studio`;
  return {
    home: root,
    // home/generate_microreel belongs to the platform's New microreel page.
    create: `${root}/new`,
    job: (id: string, step = 'storyboard') =>
      `${root}/microreels/${encodeURIComponent(id)}/${step}`,
    published: (courseId: string) =>
      `/manage/${organizationId}/microreels/${courseId}`,
    /** Where the workspace buys more AI credits: the add-ons of Settings > AI. */
    aiCredits: `/${organizationId}/marketplace`,
  };
}

export function useStudioPaths() {
  return studioPaths(useParams().organizationId);
}

/**
 * Where the author can buy more AI credits, or undefined when they cannot: the marketplace
 * needs Manage settings, which Studio's authors may not have, so their pages ask them to go
 * to their workspace administrator instead. Read from the registered navigation store.
 */
export function useAddCreditsPath(): string | undefined {
  const {navigationStore} = useContext(MobXProviderContext) as {
    navigationStore?: Pick<NavigationStore, 'userPermissions'>;
  };
  const {aiCredits} = useStudioPaths();
  return navigationStore?.userPermissions.includes(
    APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
  )
    ? aiCredits
    : undefined;
}

/** The organization of the Studio page at `pathname`, which Studio requests go to. */
export function studioOrganization(pathname = window.location.pathname) {
  return pathname.match(/^\/manage\/([^/]+)(?:\/|$)/)?.[1];
}

/** Where the current organization's Studio serves its files (video, posters, images). */
export function studioApiBase(pathname = window.location.pathname) {
  const organization = studioOrganization(pathname);
  return `${(process.env.REACT_APP_API_URL || '/api').replace(
    /\/$/,
    '',
  )}/studio${organization ? `/${organization}` : ''}`;
}

/** Absolute API assets remain in the current authenticated organization. */
export const mediaUrl = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  return new URL(
    `${studioApiBase()}/${path.replace(/^\//, '')}`,
    window.location.href,
  ).href;
};
